import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AUTOMATION_STUDIO_ROUTES } from '../../features/automationStudioNew/automationStudioRoutes';
import { AI_FEATURE_KEYS } from '../../enums/AIModelEnums';
import { userCanSelectAiModel } from '../../utils/commonfunctions';
import AutomationStudioLayout from './components/layout/AutomationStudioLayout';
import ActionBar from './components/actionBar/ActionBar';
import LeftPanel from './components/leftPanel/LeftPanel';
import RightPanelContainer from './components/rightPanel/RightPanelContainer';
import AutomationPagePopup from './components/popups/AutomationPagePopup';
import { AUTOMATION_STUDIO_LOGIN_EXAMPLE } from './content';
import {
    startAutomation,
    setInstructions,
    clearInstructionEditorText,
    selectStep,
    selectStepWithModifier,
    navigateToInstructions,
    navigateToExecution,
    updateStepInPlan,
    setSelectedEnvironment,
    setSelectedAIModel,
    resetStudioNew,
    resetForNewTestCase,
    setHasEnteredInstructionsMode,
    resetPlanToSnapshot,
} from '../../features/automationStudioNew/automationStudioNewSlice';
import {
    selectInstructions,
    selectSelectedStep,
    selectRightPanelView,
    selectIsGenerating,
    selectIsReplayExecuting,
    selectIsSaving,
    selectIsSavedExecutionRecord,
    selectHasInstructions,
    selectHasAISteps,
    selectCanSave,
    selectPlanIsDirty,
    selectInstructionsChangedSinceSteps,
    selectInstructionHeadingsSnapshot,
    selectInstructionsForLeftPanel,
    selectUsesLeftPanelGroupSnapshot,
    selectUi,
    selectGenerationRun,
    selectReplayRun,
    selectInstructionsForExecutionView,
    selectFlatStepsForExecutionView,
    selectReplayProgressMap,
    selectCurrentPlanReplayProgressMap,
    selectReplayExecResults,
    selectReplayCurrentStepIndex,
    selectIsExecuting,
    selectActiveExecutionLogs,
    selectGenerationOverallStatus,
    selectReplayOverallStatus,
    selectGenerationExecutionResults,
    selectWorkflowError,
    selectValidationErrorsByStepId,
    selectSavedPlan,
    selectWorkflowMode,
    selectDynamicResult,
    selectCanExport,
    selectCanImport,
    selectCanUploadFile,
    selectIsNlpAutomatedTestCase,
} from '../../features/automationStudioNew/automationStudioNewSelectors';
import { getActiveFlatSteps, hasGenerationSessionArtifacts } from '../../features/automationStudioNew/planUtils';
import { WORKFLOW_MODE, RUN_STATUS } from '../../features/automationStudioNew/constants';
import useAutomationStudioNewInit from '../../features/automationStudioNew/hooks/useAutomationStudioNewInit';
import useAutomationStudioNewActions from '../../features/automationStudioNew/hooks/useAutomationStudioNewActions';
import useAutomationStudioNewStepHandlers from '../../features/automationStudioNew/hooks/useAutomationStudioNewStepHandlers';
import useAutomationStudioNewGenerationStream from '../../features/automationStudioNew/hooks/useAutomationStudioNewGenerationStream';
import useAutomationStudioNewImportExport from '../../features/automationStudioNew/hooks/useAutomationStudioNewImportExport';
import useAutomationStudioRouteSession from '../../features/automationStudioNew/hooks/useAutomationStudioRouteSession';
import { writeAutomationStudioLastRoute } from '../../features/automationStudioNew/automationStudioSession';
import { canCopySelection } from '../../features/automationStudioNew/selectionSelectors';
import { useTestCaseSearch } from '../AutomationStudio/hooks/useTestCaseSearch';
import { copyStepsAction } from '../../utils/automation/copySteps';
import { pasteStepsNew } from '../../features/automationStudioNew/pasteStepsNew';
import { readSteps as clipboardReadSteps } from '../../utils/automation/clipboardService';
import {
    parseInstructionClipboard,
    extractTextFromClipboardData,
    shouldIgnoreInstructionDocumentPaste,
    serializeInstructionsToClipboard,
} from '../../utils/automation/instructionClipboard';
import { useSnackbar } from '../../utils/useSnackbar';
import { showSnackbar } from '../../features/snakbar/snackbarSlice';
import { TOASTS } from '../../utils/automation/copyPasteConstants';
import { useStore } from 'react-redux';
import { clearSelection } from '../../features/automationStudioNew/automationStudioNewSlice';
import QuickFileUploadModal from '../(Automation)/RecordPage/QuickFileUploadModal';

/**
 * Redux-connected Automation Studio (Phase 2).
 * Route: /automation-studio
 */
export default function AutomationStudioContainer() {
    const dispatch = useDispatch();
    const store = useStore();
    const navigate = useNavigate();
    const params = useParams();
    const { showSnackbar: showToast } = useSnackbar();
    const { selectedProject } = useSelector((state) => state.projects);
    const { user } = useSelector((state) => state.auth);

    const [savePopup, setSavePopup] = useState(null);
    const [savePopupData, setSavePopupData] = useState({});

    const projectId = selectedProject?.id;
    const orgId = selectedProject?.org_id;
    const userId = user?.id;
    const testCaseId = params.test_case_id || '';

    const instructions = useSelector(selectInstructions);
    const executionInstructions = useSelector(selectInstructionsForExecutionView);
    const selectedStep = useSelector(selectSelectedStep);
    const rightPanelView = useSelector(selectRightPanelView);
    const isGenerating = useSelector(selectIsGenerating);
    const replayExecuting = useSelector(selectIsReplayExecuting);
    const isSaving = useSelector(selectIsSaving);
    const isSavedExecutionRecord = useSelector(selectIsSavedExecutionRecord);
    const hasInstructions = useSelector(selectHasInstructions);
    const hasAISteps = useSelector(selectHasAISteps);
    const canSave = useSelector(selectCanSave);
    const planIsDirty = useSelector(selectPlanIsDirty);
    const instructionsChangedSinceSteps = useSelector(selectInstructionsChangedSinceSteps);
    const instructionHeadingsSnapshot = useSelector(selectInstructionHeadingsSnapshot);
    const ui = useSelector(selectUi);
    const generationRun = useSelector(selectGenerationRun);
    const replayRun = useSelector(selectReplayRun);
    const replayProgressMap = useSelector(selectReplayProgressMap);
    const currentReplayProgressMap = useSelector(selectCurrentPlanReplayProgressMap);
    const replayExecutionResults = useSelector(selectReplayExecResults);
    const replayCurrentStepIndex = useSelector(selectReplayCurrentStepIndex);
    const isExecuting = useSelector(selectIsExecuting);
    const generationOverallStatus = useSelector(selectGenerationOverallStatus);
    const replayOverallStatus = useSelector(selectReplayOverallStatus);
    const generationExecutionResults = useSelector(selectGenerationExecutionResults);
    const executionLogs = useSelector(selectActiveExecutionLogs);
    const workflowError = useSelector(selectWorkflowError);
    const stepValidationErrors = useSelector(selectValidationErrorsByStepId);
    const savedPlan = useSelector(selectSavedPlan);
    const keepRecordingExecutionUi = useSelector(
        (state) => state.automationStudioNew?.planDraft?.keepRecordingExecutionUi ?? false
    );
    const justSavedInSession = useSelector(
        (state) => state.automationStudioNew?.planDraft?.justSavedInSession ?? false
    );
    const workflowMode = useSelector(selectWorkflowMode);
    const dynamicResult = useSelector(selectDynamicResult);
    const canCopy = useSelector(canCopySelection);
    const canExport = useSelector((state) => selectCanExport(state, testCaseId));
    const isNlpAutomated = useSelector(selectIsNlpAutomatedTestCase);
    const canImport = useSelector(selectCanImport);
    const canUploadFile = useSelector((state) => selectCanUploadFile(state, projectId));
    const selectedStepIds = useSelector(
        (state) => state.automationStudioNew?.selection?.selectedStepIds ?? []
    );
    const mcpToolsList = useSelector((state) => state.automationStudioNew?.mcpToolsList ?? []);
    const mcpToolMap = useSelector((state) => state.automationStudioNew?.mcpToolMap ?? {});
    const fileSuggestions = useSelector((state) => state.automationStudioNew?.fileSuggestions ?? []);
    const fileDescriptions = useSelector((state) => state.automationStudioNew?.fileDescriptions ?? {});
    const variablesList = useSelector((state) => state.replay?.variablesList ?? []);
    const variablesListDescriptions = useSelector(
        (state) => state.replay?.variablesListDescriptions ?? {}
    );
    const isLoadingSavedPlan = useSelector(
        (state) => state.automationStudioNew?.savedPlan?.isLoading ?? false
    );

    const canSelectAiModel = userCanSelectAiModel(user, projectId);
    const isTrialOrg = user?.plan_name === 'trial';

    const {
        environments,
        testCaseKey,
        isInitialLoading,
        refetchFiles,
        reloadTestCase,
        selectedTestCaseOption,
    } = useAutomationStudioNewInit({
        projectId,
        testCaseId,
    });

    const {
        testCaseOptions,
        loading: isLoadingTestCases,
        apiError: testCaseApiError,
        hasMore: hasMoreTestCases,
        handleInputChange: onTestCaseInputChange,
        handleDropdownScroll: onTestCaseDropdownScroll,
        handleKeyboardNavigation: onTestCaseKeyboardNavigation,
    } = useTestCaseSearch({
        restrictToUrlTestCase: false,
        selectedTestCaseOption,
    });

    const handleOpenSavePopup = useCallback((data) => {
        setSavePopupData(data);
        setSavePopup('saveExecutionRecord');
    }, []);

    const handleOpenConfirmSavePopup = useCallback((data) => {
        setSavePopupData({
            failedSteps: data?.failedSteps ?? [],
            variant: data?.variant ?? 'default',
        });
        setSavePopup('confirmSaveExecutionRecord');
    }, []);

    const {
        handleGenerate,
        handleReplay,
        handleSave,
        handleSaveAndReplay,
        handleStopExecution,
        confirmPendingSaveAfterRecordingFailures,
        cancelPendingSaveAfterRecordingFailures,
        activeRunIdRef,
        clearAutomationData,
    } = useAutomationStudioNewActions({
        projectId,
        orgId,
        userId,
        testCaseId,
        testCaseKey: testCaseKey ?? savedPlan.testCaseKey,
        isTrialOrg,
        canSelectAiModel,
        onOpenSavePopup: handleOpenSavePopup,
        onOpenConfirmSavePopup: handleOpenConfirmSavePopup,
    });

    const closeSavePopup = useCallback(() => {
        cancelPendingSaveAfterRecordingFailures();
        setSavePopup(null);
        setSavePopupData({});
    }, [cancelPendingSaveAfterRecordingFailures]);

    const handleConfirmSaveExecutionRecord = useCallback(async () => {
        try {
            return await confirmPendingSaveAfterRecordingFailures();
        } catch (error) {
            console.error('[AutomationStudioNew] Confirm save failed', error);
            return false;
        }
    }, [confirmPendingSaveAfterRecordingFailures]);

    const {
        handleAddStep,
        handleDeleteStep,
        handlePrevStep,
        handleNextStep,
        handleCopy,
        handlePasteAbove,
        handlePasteBelow,
        getCopyPasteState,
    } = useAutomationStudioNewStepHandlers({ testCaseId });

    useAutomationStudioNewGenerationStream();

    const {
        importInputRef,
        showQuickUploadModal,
        setShowQuickUploadModal,
        handleExport,
        handleImportFile,
        handleImportClick,
        handleUploadSuccess,
    } = useAutomationStudioNewImportExport({
        testCaseId: testCaseId || savedPlan.testCaseId,
        onRefetchFiles: refetchFiles,
    });

    const handleKeyCopyPaste = useCallback(
        async (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && (e.key === 'c' || e.key === 'C')) {
                e.preventDefault();
                await copyStepsAction({ getState: getCopyPasteState, dispatch: store.dispatch });
            } else if (mod && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
                e.preventDefault();
                const anchor = store.getState().automationStudioNew.selection.selectionAnchorId;
                if (!anchor) {
                    store.dispatch(
                        showSnackbar({ message: TOASTS.PASTE_ABOVE_NEEDS_ANCHOR, severity: 'info', duration: 4000 })
                    );
                    return;
                }
                const env = await clipboardReadSteps();
                if (!env) {
                    store.dispatch(
                        showSnackbar({ message: TOASTS.PASTE_UNRECOGNIZED, severity: 'warning', duration: 4000 })
                    );
                    return;
                }
                pasteStepsNew({
                    envelope: env,
                    position: 'above',
                    anchorStepId: anchor,
                    getState: () => ({
                        instructions: store.getState().automationStudioNew.planDraft.instructions,
                    }),
                    dispatch: store.dispatch,
                });
            } else if (mod && (e.key === 'v' || e.key === 'V')) {
                e.preventDefault();
                const env = await clipboardReadSteps();
                if (!env) {
                    store.dispatch(
                        showSnackbar({ message: TOASTS.PASTE_UNRECOGNIZED, severity: 'warning', duration: 4000 })
                    );
                    return;
                }
                const anchor = store.getState().automationStudioNew.selection.selectionAnchorId;
                pasteStepsNew({
                    envelope: env,
                    position: anchor ? 'below' : 'append-end',
                    anchorStepId: anchor,
                    getState: () => ({
                        instructions: store.getState().automationStudioNew.planDraft.instructions,
                    }),
                    dispatch: store.dispatch,
                });
            } else if (e.key === 'Escape') {
                dispatch(clearSelection());
            }
        },
        [dispatch, getCopyPasteState, store]
    );

    const handleCopyInstructions = useCallback(() => {
        const text = serializeInstructionsToClipboard(instructions);
        navigator.clipboard.writeText(text).then(() => {
            showToast('Instructions copied to clipboard', 'success');
        }).catch(() => {
            showToast('Failed to copy instructions', 'error');
        });
    }, [instructions, showToast]);

    const applyPastedInstructionText = useCallback(
        (text) => {
            const trimmed = String(text ?? '').trim();
            if (!trimmed) {
                showToast('Invalid format or empty clipboard', 'warning');
                return false;
            }

            const { valid, instructions: parsed } = parseInstructionClipboard(trimmed);

            let newInstructions = [];

            if (valid && parsed?.length) {
                newInstructions = parsed.map((item, index) => ({
                    id: Date.now() + index,
                    text: item.text,
                    aiSteps: [],
                }));
            } else {
                const lines = trimmed
                    .split(/\r?\n/)
                    .map((line) => line.trim())
                    .filter(Boolean);

                if (!lines.length) {
                    showToast('Invalid format or empty clipboard', 'warning');
                    return false;
                }

                newInstructions = lines.map((line, index) => ({
                    id: Date.now() + index,
                    text: line,
                    aiSteps: [],
                }));
            }

            dispatch(setInstructions(newInstructions));
            if (!testCaseId && !ui.hasEnteredInstructionsMode) {
                dispatch(navigateToInstructions());
                dispatch(setHasEnteredInstructionsMode(true));
            }
            showToast(
                `Loaded ${newInstructions.length} instruction${newInstructions.length === 1 ? '' : 's'}`,
                'success'
            );
            return true;
        },
        [dispatch, showToast, testCaseId, ui.hasEnteredInstructionsMode]
    );

    const handlePasteInstructions = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText();
            applyPastedInstructionText(text);
        } catch {
            showToast('Could not read clipboard', 'error');
        }
    }, [applyPastedInstructionText, showToast]);

    useAutomationStudioRouteSession({ clearAutomationData });

    const prevProjectIdRef = useRef(projectId);
    useEffect(() => {
        if (prevProjectIdRef.current !== projectId) {
            dispatch(resetStudioNew());
            writeAutomationStudioLastRoute(window.location.pathname || '');
            prevProjectIdRef.current = projectId;
        }
    }, [projectId, dispatch]);

    useEffect(() => {
        if (ui.selectedEnvironment) {
            localStorage.setItem('automation_studio_environment', ui.selectedEnvironment);
        }
    }, [ui.selectedEnvironment]);

    const showEmptyRightPanel = !testCaseId && !ui.hasEnteredInstructionsMode;

    useEffect(() => {
        if (!showEmptyRightPanel) return undefined;

        const handleEmptyPanelPaste = (event) => {
            if (shouldIgnoreInstructionDocumentPaste(event.target)) {
                return;
            }

            const text = extractTextFromClipboardData(event.clipboardData);
            if (!text.trim()) return;

            event.preventDefault();
            applyPastedInstructionText(text);
        };

        document.addEventListener('paste', handleEmptyPanelPaste);
        return () => document.removeEventListener('paste', handleEmptyPanelPaste);
    }, [showEmptyRightPanel, applyPastedInstructionText]);
    const showSessionExecutionUi =
        keepRecordingExecutionUi || generationRun.status === RUN_STATUS.COMPLETED;
    const instructionsForDisplay = useMemo(() => {
        if (isGenerating) {
            return instructions;
        }
        return showSessionExecutionUi ? executionInstructions : instructions;
    }, [isGenerating, showSessionExecutionUi, executionInstructions, instructions]);
    const instructionsForLeftPanel = useSelector(selectInstructionsForLeftPanel);
    const usesLeftPanelGroupSnapshot = useSelector(selectUsesLeftPanelGroupSnapshot);
    const flatStepsForReplay = useMemo(
        () => getActiveFlatSteps(instructionsForLeftPanel),
        [instructionsForLeftPanel]
    );
    const flatStepsForExecution = useSelector(selectFlatStepsForExecutionView);

    const hasGenerationPartialResults = useMemo(
        () =>
            hasGenerationSessionArtifacts({
                generationRun,
                planDraft: { instructions },
            }),
        [generationRun, instructions]
    );

    const hasReplayProgress = Object.keys(replayProgressMap).length > 0;
    const isReplayFlow =
        replayExecuting ||
        replayRun.status === RUN_STATUS.COMPLETED ||
        replayRun.status === RUN_STATUS.STOPPED ||
        replayRun.status === RUN_STATUS.FAILED ||
        hasReplayProgress;

    // FAILED must count when streamed steps already exist: the agent emits `error`
    // (generationFailed) before `testComplete`. Without this, isSavedPlanIdle flips true
    // for all-failed recorder steps and Step Results briefly shows
    // "Skipped — will not run during replay" until testComplete arrives.
    const hasExecutedInSession = Boolean(
        generationRun.status === RUN_STATUS.COMPLETED ||
        (generationRun.status === RUN_STATUS.STOPPED && hasGenerationPartialResults) ||
        (generationRun.status === RUN_STATUS.FAILED && hasGenerationPartialResults) ||
        replayRun.status === RUN_STATUS.COMPLETED ||
        replayRun.status === RUN_STATUS.RUNNING ||
        hasReplayProgress ||
        keepRecordingExecutionUi ||
        justSavedInSession
    );

    const isLoadedFromTestcase = Boolean(
        savedPlan.isSaved && !hasExecutedInSession && !isGenerating && !replayExecuting
    );

    const isEmptyState =
        workflowMode === WORKFLOW_MODE.EMPTY && !hasInstructions && !testCaseId;

    const navigateToTestCase = useCallback(
        (id) => {
            navigate(
                generatePath(AUTOMATION_STUDIO_ROUTES.testCase, {
                    project_id: params.project_id,
                    test_case_id: id,
                })
            );
        },
        [navigate, params.project_id]
    );

    const handleTestCaseSelect = useCallback(
        (newTestCaseId) => {
            const normalizedId = newTestCaseId ? String(newTestCaseId) : null;
            const currentId = testCaseId ? String(testCaseId) : null;

            if (normalizedId === currentId) {
                dispatch(navigateToInstructions());
                return;
            }

            dispatch(resetForNewTestCase());

            if (normalizedId) {
                navigateToTestCase(normalizedId);
                return;
            }

            navigate(
                generatePath(AUTOMATION_STUDIO_ROUTES.base, {
                    project_id: params.project_id,
                })
            );
        },
        [dispatch, navigate, navigateToTestCase, params.project_id, testCaseId]
    );

    const handleClearInstructions = useCallback(() => {
        dispatch(clearInstructionEditorText());
        dispatch(navigateToInstructions());
    }, [dispatch]);

    const handleStartAutomation = useCallback(() => {
        dispatch(startAutomation());
        dispatch(setHasEnteredInstructionsMode(true));
    }, [dispatch]);

    const handleTryLoginExample = useCallback(() => {
        const baseId = Date.now();
        dispatch(
            setInstructions(
                AUTOMATION_STUDIO_LOGIN_EXAMPLE.instructions.map((inst, index) => ({
                    id: baseId + index,
                    text: inst.text,
                    aiSteps: [],
                }))
            )
        );
        dispatch(navigateToInstructions());
        dispatch(setHasEnteredInstructionsMode(true));
    }, [dispatch]);

    const handleStepSelect = useCallback(
        (step, event) => {
            if (!step?.id) return;
            const shiftKey = Boolean(event?.shiftKey);
            const toggleKey = Boolean(event?.metaKey || event?.ctrlKey);
            if (shiftKey || toggleKey) {
                dispatch(
                    selectStepWithModifier({
                        stepId: step.id,
                        shiftKey,
                        metaKey: event?.metaKey,
                        ctrlKey: event?.ctrlKey,
                    })
                );
            } else {
                dispatch(selectStep(step.id));
            }
        },
        [dispatch]
    );

    const handleStepIdSelect = useCallback(
        (stepId) => {
            if (stepId) dispatch(selectStep(stepId));
        },
        [dispatch]
    );

    const handleReset = useCallback(async () => {
        if (testCaseId) {
            if (savedPlan.lastSavedSnapshot?.instructions?.length) {
                dispatch(resetPlanToSnapshot());
            } else {
                await reloadTestCase(testCaseId);
            }
            dispatch(navigateToInstructions());
            dispatch(clearSelection());
            return;
        }
        dispatch(resetForNewTestCase());
        navigate(
            generatePath(AUTOMATION_STUDIO_ROUTES.base, {
                project_id: params.project_id,
            })
        );
    }, [
        dispatch,
        navigate,
        params.project_id,
        reloadTestCase,
        savedPlan.lastSavedSnapshot,
        testCaseId,
    ]);

    const executionContext = useMemo(
        () => ({
            projectId,
            organizationId: orgId,
            userId,
        }),
        [projectId, orgId, userId]
    );

    if (isInitialLoading && testCaseId && !instructions.length) {
        return null;
    }

    return (
        <>
            <AutomationStudioLayout
                leftPanel={(
                    <LeftPanel
                        instructions={instructionsForLeftPanel}
                        isGenerating={isGenerating}
                        isLoadingSavedPlan={isLoadingSavedPlan}
                        selectedStep={selectedStep}
                        selectedStepIds={selectedStepIds}
                        isRunning={isExecuting}
                        isReplayExecuting={replayExecuting}
                        isReplayFlow={isReplayFlow}
                        isLoadedFromTestcase={isLoadedFromTestcase}
                        usesLeftPanelGroupSnapshot={usesLeftPanelGroupSnapshot}
                        hasExecutedInSession={hasExecutedInSession}
                        replayProgressMap={currentReplayProgressMap}
                        replayExecutionResults={replayExecutionResults}
                        replayCurrentStepIndex={replayCurrentStepIndex}
                        executionFlow={generationRun.executionFlow}
                        fileSuggestions={fileSuggestions}
                        variablesList={variablesList}
                        variablesListDescriptions={variablesListDescriptions}
                        instructionHeadingsSnapshot={instructionHeadingsSnapshot}
                        stepValidationErrors={stepValidationErrors}
                        canCopy={canCopy}
                        canPaste
                        onStepSelect={handleStepSelect}
                        onStepIdSelect={handleStepIdSelect}
                        onInstructionSelect={() => dispatch(navigateToInstructions())}
                        onAddStep={handleAddStep}
                        onCopy={handleCopy}
                        onPasteAbove={handlePasteAbove}
                        onPasteBelow={handlePasteBelow}
                        onKeyCopyPaste={handleKeyCopyPaste}
                    />
                )}
                rightPanel={(
                    <RightPanelContainer
                            state={rightPanelView}
                            selectedStep={selectedStep}
                            instructions={instructions}
                            executionInstructions={executionInstructions}
                            isGenerating={isGenerating}
                            isExecuting={isExecuting}
                            isReplayFlow={isReplayFlow}
                            replayExecuting={replayExecuting}
                            replayProgressMap={currentReplayProgressMap}
                            executionReplayProgressMap={replayProgressMap}
                            replayExecutionResults={replayExecutionResults}
                            replayCurrentStepIndex={replayCurrentStepIndex}
                            flatStepsForReplay={flatStepsForExecution}
                            screenshots={isReplayFlow ? [] : generationRun.screenshots}
                            logs={executionLogs}
                            sessionId={isReplayFlow ? replayRun.runId : generationRun.runId}
                            executionFlow={isReplayFlow ? null : generationRun.executionFlow}
                            testCompleted={
                                isReplayFlow
                                    ? (replayExecuting || hasReplayProgress || replayRun.status === RUN_STATUS.COMPLETED)
                                    : (
                                        generationRun.status === RUN_STATUS.COMPLETED ||
                                        (generationRun.status === RUN_STATUS.STOPPED && hasGenerationPartialResults) ||
                                        (generationRun.status === RUN_STATUS.FAILED && hasGenerationPartialResults) ||
                                        hasReplayProgress
                                    )
                            }
                            overallStatus={isReplayFlow ? replayOverallStatus : generationOverallStatus}
                            executionResults={isReplayFlow ? null : generationExecutionResults}
                            isLoadedFromTestcase={isLoadedFromTestcase}
                            hasExecutedInSession={hasExecutedInSession}
                            isSavedExecutionRecord={isSavedExecutionRecord}
                            automationError={workflowError}
                            fileSuggestions={fileSuggestions}
                            fileDescriptions={fileDescriptions}
                            mcpToolsList={mcpToolsList}
                            mcpToolMap={mcpToolMap}
                            variablesList={variablesList}
                            variablesListDescriptions={variablesListDescriptions}
                            isInitialDataLoading={isInitialLoading}
                            isLoadingSavedPlan={isLoadingSavedPlan}
                            showEmptyRightPanel={showEmptyRightPanel}
                            selectedTestCaseId={testCaseId || savedPlan.testCaseId || ''}
                            testCaseKey={testCaseKey ?? savedPlan.testCaseKey}
                            testCaseOptions={testCaseOptions}
                            isLoadingTestCases={isLoadingTestCases}
                            testCaseApiError={testCaseApiError}
                            hasMoreTestCases={hasMoreTestCases}
                            onTestCaseInputChange={onTestCaseInputChange}
                            onTestCaseDropdownScroll={onTestCaseDropdownScroll}
                            onTestCaseKeyboardNavigation={onTestCaseKeyboardNavigation}
                            onTestCaseSelect={handleTestCaseSelect}
                            onInstructionsChange={(next) => dispatch(setInstructions(next))}
                            onClearInstructions={handleClearInstructions}
                            onCopyInstructions={handleCopyInstructions}
                            onPasteInstructions={handlePasteInstructions}
                            onStepUpdate={(updated) => dispatch(updateStepInPlan(updated))}
                            onBackToInstructions={() => dispatch(navigateToInstructions())}
                            onBackToResults={() => dispatch(navigateToExecution())}
                        onPrevStep={handlePrevStep}
                        onNextStep={handleNextStep}
                        onDeleteStep={handleDeleteStep}
                            onRunAgain={handleGenerate}
                            onEditInstructions={() => dispatch(navigateToInstructions())}
                            onStartAutomation={handleStartAutomation}
                            onTryLoginExample={handleTryLoginExample}
                            onStopExecution={() =>
                                handleStopExecution(activeRunIdRef.current, executionContext)
                            }
                            onSaveExecutionRecord={handleSave}
                            stepValidationErrors={stepValidationErrors}
                            dynamicResult={dynamicResult}
                            showDynamicScriptChip={Boolean(dynamicResult?.dynamicStepIds?.length)}
                            canExport={canExport}
                            canImport={canImport}
                            onExport={handleExport}
                            onImportClick={handleImportClick}
                            isNlpAutomated={isNlpAutomated}
                            isExportBusy={isGenerating || replayExecuting || isSaving}
                            canUploadFile={canUploadFile}
                            onUploadClick={() => setShowQuickUploadModal(true)}
                    />
                )}
                actionBar={(
                    <ActionBar
                        isEmptyState={isEmptyState}
                        hasInstructions={hasInstructions}
                        environments={environments}
                        selectedEnvironment={ui.selectedEnvironment}
                        selectedAIModel={ui.selectedAIModel}
                        isGenerating={isGenerating}
                        isSaving={isSaving}
                        isSavedExecutionRecord={isSavedExecutionRecord}
                        replayExecuting={replayExecuting}
                        canSave={canSave}
                        onEnvironmentChange={(env) => dispatch(setSelectedEnvironment(env))}
                        onModelChange={(model) => {
                            if (model?.provider && model?.model_key) {
                                dispatch(
                                    setSelectedAIModel(
                                        JSON.stringify({
                                            provider: model.provider,
                                            model_key: model.model_key,
                                        })
                                    )
                                );
                            }
                        }}
                        studioFeatureKey={
                            testCaseId
                                ? AI_FEATURE_KEYS.REPLAY_FLOW
                                : AI_FEATURE_KEYS.AUTOMATION_RECORDING
                        }
                        trialOrgSkip={isTrialOrg}
                        canSelectAiModel={canSelectAiModel && !isTrialOrg}
                        projectId={params.project_id}
                        onGenerate={handleGenerate}
                        onReplay={handleReplay}
                        onSaveAndReplay={handleSaveAndReplay}
                        onStopExecution={() =>
                            handleStopExecution(activeRunIdRef.current, executionContext)
                        }
                        onSave={handleSave}
                        onReset={handleReset}
                        hasAISteps={hasAISteps}
                        hasSelectedTestCase={Boolean(testCaseId || savedPlan.testCaseId)}
                        instructionsSyncedWithSteps={!instructionsChangedSinceSteps}
                        planIsDirty={planIsDirty}
                    />
                )}
            />
            <input
                type="file"
                ref={importInputRef}
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleImportFile}
            />
            {showQuickUploadModal && (
                <QuickFileUploadModal
                    onSuccess={handleUploadSuccess}
                    onClose={() => setShowQuickUploadModal(false)}
                />
            )}
            <AutomationPagePopup
                popup={savePopup}
                popupData={{
                    ...savePopupData,
                    testCaseId,
                    togglePopup: closeSavePopup,
                    onSuccess: savePopupData?.onSuccess,
                }}
                onConfirmSave={handleConfirmSaveExecutionRecord}
                onClose={closeSavePopup}
            />
        </>
    );
}
